import React, {useCallback, useMemo, useState} from 'react';
import {Stack, Typography} from '@mui/material';
import {useLocation} from 'react-router';
import useCollectionMaterialViewer from '../../collection/viewer/useCollectionMaterialViewer';
import useCoverageModalUrlState from '../coverage/useCoverageModalUrlState';
import {COVERAGE_SECTIONS} from '../coverage/coverageModalUrlState';
import {
    COLLECTION_EEI_STATUS,
} from '../coverage/buildCollectionCoverageView';
import {matchCollectionEeiSegment} from './EeiCoverageStatusBadges';
import CoverageSnapshotHeader from '../coverage/components/CoverageSnapshotHeader';
import CoverageSourceLens from '../coverage/components/CoverageSourceLens';
import CoverageItemCard from '../coverage/components/CoverageItemCard';
import CoverageMaterialRow from '../coverage/components/CoverageMaterialRow';
import FintelEvidenceSections from '../coverage/components/FintelEvidenceSections';

function normText(s) {
    return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function matchIcrCollectionRequirement(requirements, questionText) {
    if (!Array.isArray(requirements) || requirements.length === 0) {
        return null;
    }
    const scalar = requirements.filter(
        (r) => r && r.evaluatorKind !== 'COMPLIANCE' && r.countsTowardScalar !== false,
    );
    const pool = scalar.length ? scalar : requirements;
    const icrReqs = pool.filter((r) => r.eeiIndex == null);
    const needle = normText(questionText);
    if (needle) {
        const byText = icrReqs.find((r) => normText(r.text) === needle);
        if (byText) return byText;
    }
    return icrReqs[0] || null;
}

function materialsFromRequirement(req, materialIndex) {
    if (!req) return [];
    const ids = [
        ...(req.linkedSupportingMaterialIds || []),
        ...(req.unlinkedSupportingMaterialIds || []),
    ];
    const seen = new Set();
    const out = [];
    for (const id of ids) {
        const key = String(id);
        if (seen.has(key)) continue;
        seen.add(key);
        const material = materialIndex?.get(key);
        if (material) out.push(material);
    }
    return out;
}

function buildMaterialIndex(collectionView) {
    const map = new Map();
    if (!collectionView) return map;
    for (const material of collectionView.linked?.materials || []) {
        if (material?.id) map.set(String(material.id), material);
    }
    for (const material of collectionView.unlinked?.materials || []) {
        if (material?.id && !map.has(String(material.id))) {
            map.set(String(material.id), material);
        }
    }
    for (const material of collectionView.fintelUsage?.materials || []) {
        if (material?.id && !map.has(String(material.id))) {
            map.set(String(material.id), material);
        }
    }
    return map;
}

export function materialCountLabel(materials) {
    const list = Array.isArray(materials) ? materials : [];
    if (list.length === 0) return 'No collection yet';
    const cms = list.filter((m) => m.materialKind === 'cm').length;
    const iirs = list.filter((m) => m.materialKind === 'iir').length;
    const other = list.length - cms - iirs;
    const parts = [];
    if (cms) parts.push(`${cms} CM${cms === 1 ? '' : 's'}`);
    if (iirs) parts.push(`${iirs} IIR${iirs === 1 ? '' : 's'}`);
    if (other) parts.push(`${other} collection item${other === 1 ? '' : 's'}`);
    return parts.join(', ');
}

export function productCountLabel(fintels, coveragePct = null) {
    const list = Array.isArray(fintels) ? fintels : [];
    if (list.length === 0) {
        // A score without listed FINTELs is still coverage (ICR-level products).
        return coveragePct != null ? null : 'No products answering yet';
    }
    const linked = list.filter((fintel) => fintel.linked).length;
    const suggested = list.length - linked;
    const parts = [];
    if (linked) parts.push(`${linked} linked`);
    if (suggested) parts.push(`${suggested} suggested`);
    return parts.join(', ') || null;
}

/** EEI panes inherit ICR-level FINTELs when the EEI is scored but has no own refs. */
export function fintelsForAnalyticPane(rowFintels, coveragePct, icrFintels = []) {
    const own = Array.isArray(rowFintels) ? rowFintels : [];
    if (own.length > 0) return own;
    if (coveragePct != null && icrFintels.length > 0) return icrFintels;
    return own;
}

export function hasScoredAnalytics(detailCoverage) {
    // Headline % is the same value the Overview ring uses; show it even when
    // icr_questions rows were not persisted (question coveragePct is then null).
    if (detailCoverage?.overallCoveragePct != null) return true;
    if (detailCoverage?.question?.coveragePct != null) return true;
    return (detailCoverage?.eeis || []).some((row) => row.coveragePct != null);
}

export function dataHeaderCaption(segments) {
    if (!Array.isArray(segments) || segments.length === 0) {
        return 'No collection yet';
    }
    const withEvidence = segments.filter(
        (s) => s.status && s.status !== COLLECTION_EEI_STATUS.UNCOVERED,
    ).length;
    if (withEvidence === 0) return 'No collection yet';
    return `${withEvidence} of ${segments.length} EEIs have collection evidence`;
}

export function analyticHeaderCaption(detailCoverage) {
    const linked = Number(detailCoverage?.linkedFintelCount);
    const suggested = Number(detailCoverage?.discoveredFintelCount);
    const parts = [];
    if (Number.isFinite(linked) && linked > 0) parts.push(`${linked} linked`);
    if (Number.isFinite(suggested) && suggested > 0) parts.push(`${suggested} suggested`);
    if (parts.length) return parts.join(' · ');
    const questionFintels = detailCoverage?.question?.fintels || [];
    const eeiFintels = (detailCoverage?.eeis || []).reduce(
        (sum, row) => sum + (row.fintels || []).length,
        0,
    );
    if (questionFintels.length + eeiFintels === 0) {
        return hasScoredAnalytics(detailCoverage) ? null : 'No products answering yet';
    }
    const eeis = detailCoverage?.eeis || [];
    const withProducts = eeis.filter((row) => (row.fintels || []).length > 0).length;
    if (withProducts === 0) {
        return hasScoredAnalytics(detailCoverage) ? null : 'No products answering yet';
    }
    return `${withProducts} of ${eeis.length} EEIs have answering products`;
}

function evidenceRows(items, renderItem) {
    if (!items || items.length === 0) return null;
    return items.map(renderItem);
}

/**
 * ICR detail → Coverage tab: Data vs Analytics (Products) snapshot and
 * per-item cards for the ICR question and each EEI.
 */
export default function IcrCoverageTab({
    detailCoverage,
    collectionView = null,
    collectionSegments = [],
    collectionEeiRows = [],
    overallCollectionPct = null,
    coverageSnapshot = null,
    collectionComputing = false,
    analyticComputing = false,
    questionText,
    onNavigateToEei,
}) {

    const location = useLocation();
    const {openCollectionMaterial} = useCollectionMaterialViewer();
    const returnTo = `${location.pathname}${location.search}${location.hash || ''}`;
    const {openModal} = useCoverageModalUrlState();
    const [lens, setLens] = useState('both');

    const handleOpenMaterial = useCallback((material) => {
        if (!material?.id) {
            return;
        }
        openCollectionMaterial({
            materialId: material.id,
            materialKind: material.materialKind === 'iir' ? 'iir' : 'cm',
            returnTo,
        });
    }, [openCollectionMaterial, returnTo]);

    const materialIndex = useMemo(() => buildMaterialIndex(collectionView), [collectionView]);
    const requirements = collectionView?.requirements || [];

    const question = detailCoverage?.question;
    const eeis = detailCoverage?.eeis || [];
    const analyticsScored = hasScoredAnalytics(detailCoverage);
    const analyticOverallPct = analyticsScored
        ? (detailCoverage?.overallCoveragePct ?? detailCoverage?.question?.coveragePct ?? null)
        : null;

    // Prefer caller headline (CCC nest / resolveDataHeadlinePct). Do not fall back to
    // EEI segment average — that desynced list Data % from the Coverage Total meter.
    const collectionOverallPct = overallCollectionPct;

    const icrCollectionReq = useMemo(
        () => matchIcrCollectionRequirement(requirements, questionText || question?.text),
        [requirements, questionText, question?.text],
    );

    const icrDataMaterials = useMemo(() => {
        const fromReq = materialsFromRequirement(icrCollectionReq, materialIndex);
        if (fromReq.length > 0) return fromReq;
        const fromRows = (collectionEeiRows || []).flatMap(
            (row) => [...(row.linkedMaterials || []), ...(row.unlinkedMaterials || [])],
        );
        const seen = new Set();
        return fromRows.filter((m) => {
            if (!m?.id || seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
        });
    }, [icrCollectionReq, materialIndex, collectionEeiRows]);

    const icrDataPct = icrCollectionReq?.generalScorePct
        ?? icrCollectionReq?.linkedScorePct
        ?? collectionOverallPct;

    const icrFintels = question?.fintels || [];

    return (
        <Stack spacing={3}>
            <CoverageSnapshotHeader
                dataPct={collectionOverallPct}
                dataCaption={dataHeaderCaption(collectionSegments)}
                analyticPct={analyticOverallPct}
                analyticCaption={analyticHeaderCaption(detailCoverage)}
                onShowDataDetails={() => openModal({section: COVERAGE_SECTIONS.COLLECTION})}
                onShowAnalyticsDetails={() => openModal({section: COVERAGE_SECTIONS.ANALYTIC})}
            />

            <CoverageSourceLens lens={lens} onLensChange={setLens}/>

            <Stack spacing={1.25}>
                <Typography variant="subtitle1" fontWeight={700}>This ICR</Typography>
                <CoverageItemCard
                    title={questionText || question?.text || '—'}
                    lens={lens}
                    dataCoveragePct={icrDataPct}
                    dataCaption={materialCountLabel(icrDataMaterials)}
                    dataEvidence={evidenceRows(icrDataMaterials, (material) => (
                        <CoverageMaterialRow
                            key={material.id || material.humanId}
                            material={material}
                            onOpen={handleOpenMaterial}
                        />
                    ))}
                    analyticCoveragePct={question?.coveragePct ?? analyticOverallPct}
                    analyticCaption={productCountLabel(icrFintels, question?.coveragePct ?? analyticOverallPct)}
                    analyticEvidence={icrFintels.length
                        ? <FintelEvidenceSections fintels={icrFintels}/>
                        : null}
                />
            </Stack>

            <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>EEIs</Typography>
                {eeis.length === 0 ? (
                    <Typography variant="body1" color="text.secondary">—</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {eeis.map((row, index) => {
                            const eeiKey = row.eei?.id || row.eei?.eeiNumber || row.requirementId;
                            const prefix = row.eei?.eeiNumber || row.eei?.id;
                            const collectionSegment = matchCollectionEeiSegment(collectionSegments, row.eei, index);
                            const collectionRow = collectionEeiRows.find(
                                (r) => (row.eei?.id && r.eeiId === row.eei.id)
                                    || (row.eei?.eeiNumber && r.eeiNumber === row.eei.eeiNumber)
                                    || r.eeiIndex === index + 1,
                            ) || collectionSegment;
                            const dataMaterials = [
                                ...(collectionRow?.linkedMaterials || []),
                                ...(collectionRow?.unlinkedMaterials || []),
                            ];
                            const dataPct = collectionRow?.generalScorePct
                                ?? collectionRow?.linkedScorePct
                                ?? null;
                            const fintels = fintelsForAnalyticPane(
                                row.fintels,
                                row.coveragePct,
                                icrFintels,
                            );
                            return (
                                <CoverageItemCard
                                    key={eeiKey}
                                    title={row.eei?.text || row.text || '—'}
                                    titlePrefix={prefix}
                                    onPrefixClick={prefix && onNavigateToEei
                                        ? () => onNavigateToEei(row.eei)
                                        : undefined}
                                    lens={lens}
                                    dataCoveragePct={dataPct}
                                    dataCaption={materialCountLabel(dataMaterials)}
                                    dataEvidence={evidenceRows(dataMaterials, (material) => (
                                        <CoverageMaterialRow
                                            key={material.id || material.humanId}
                                            material={material}
                                            onOpen={handleOpenMaterial}
                                        />
                                    ))}
                                    analyticCoveragePct={row.coveragePct}
                                    analyticCaption={productCountLabel(fintels, row.coveragePct)}
                                    analyticEvidence={fintels.length
                                        ? <FintelEvidenceSections fintels={fintels}/>
                                        : null}
                                />
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
}
